type ParsedApiErrorData = {
    errorCode?: unknown
    error?: string
    message?: string
    detail?: string
    [key: string]: unknown
}

function tryParseJson(text: string): ParsedApiErrorData | null {
    const trimmed = text.trim()

    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
        return null
    }

    try {
        return JSON.parse(trimmed) as ParsedApiErrorData
    } catch {
        return null
    }
}

function isArrayBufferLike(data: unknown): data is ArrayBuffer | ArrayBufferView {
    return Object.prototype.toString.call(data) === '[object ArrayBuffer]' || ArrayBuffer.isView(data)
}

function isBlobLike(data: unknown): data is Blob {
    return Object.prototype.toString.call(data) === '[object Blob]'
}

function decodeBinaryData(data: ArrayBuffer | ArrayBufferView): ParsedApiErrorData | null {
    const bytes = ArrayBuffer.isView(data) ? new Uint8Array(data.buffer, data.byteOffset, data.byteLength) : new Uint8Array(data)

    return tryParseJson(new TextDecoder('utf-8').decode(bytes))
}

async function readBlobAsText(blob: Blob): Promise<string> {
    if (typeof blob.text === 'function') {
        return blob.text()
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = () => resolve(String(reader.result ?? ''))
        reader.onerror = () => reject(reader.error)
        reader.readAsText(blob)
    })
}

export function parseAxiosErrorResponseDataSync(data: unknown): unknown {
    if (data == null) {
        return data
    }

    if (isArrayBufferLike(data)) {
        return decodeBinaryData(data) ?? data
    }

    if (typeof data === 'string') {
        return tryParseJson(data) ?? data
    }

    return data
}

export async function parseAxiosErrorResponseData(data: unknown): Promise<unknown> {
    if (data == null) {
        return data
    }

    if (isArrayBufferLike(data)) {
        return decodeBinaryData(data) ?? data
    }

    if (isBlobLike(data)) {
        const parsed = tryParseJson(await readBlobAsText(data))

        return parsed ?? data
    }

    if (typeof data === 'string') {
        return tryParseJson(data) ?? data
    }

    return data
}
